// # Revenue Analytics Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../auth/state';
import SharedHeader from '../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';
import { 
  FaChartLine, 
  FaChartPie, 
  FaChartBar,
  FaGlobe,
  FaUsers,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaClock,
  FaWallet,
  FaFilter,
  FaTimes,
  FaPercentage,
  FaDollarSign,
  FaArrowUp,
  FaArrowDown,
  FaChartArea,
  FaHeartbeat
} from 'react-icons/fa';
import './net_revenue.css';
import TrendsAnalysis from './net_revenue/trends_analysis';
import FinancialHealth from './net_revenue/financial_health';
import NetProductAnalysis from './net_revenue/net_product_analysis';
import GeoAnalysis from './net_revenue/geonalysis';
import NetRevenueExtra from './net_revenue/net_revenue_extra';

const RevenueAnalytics = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({
    startDate: new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [dateRange, setDateRange] = useState(tempDateRange);

  // # Analytics data state
  const [analyticsData, setAnalyticsData] = useState({
    revenueOverTime: [],
    monthlyGrowth: [],
    financialHealthData: [],
    stats: {
      totalRevenue: 0,
      totalBudget: 0,
      totalNetRevenue: 0,
      totalSalaryDeductions: 0,
      availableCash: 0,
      monthlyAverage: 0,
      growthRate: 0,
      projectedAnnual: 0
    }
  });

  // # Add error state
  const [error, setError] = useState(null);

  // # Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  // # Fetch analytics data
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        // Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const querySnapshot = await getDocs(financialRef);
        
        let allPayments = [];
        let allExpenses = [];
        let allSalaries = [];
        
        // Process each document (year_month format)
        querySnapshot.docs.forEach(doc => {
          const yearMonthData = doc.data();
          
          // Process payments
          if (yearMonthData.payments) {
            Object.entries(yearMonthData.payments).forEach(([paymentId, payment]) => {
              if (paymentId.startsWith('payment_') && payment.transaction_type === 'payment') {
                // Format the payment data using new structure
                const formattedPayment = {
                  id: paymentId,
                  docId: doc.id,
                  timestamp: payment.metadata?.created_at || '',
                  name: payment.user?.name || 'Unknown',
                  email: payment.user?.email || '',
                  nationality: payment.user?.nationality || 'Unknown',
                  product: payment.pay?.product || 'Unknown',
                  egp_total: Number(payment.pay?.egp_total) || 0,
                  egp_net: Number(payment.pay?.egp_net) || 0,
                  budget_dvd_amount: Number(payment.budget_dvd?.egp_amount) || 0,
                  budget_dvd_percent: Number(payment.budget_dvd?.percentage_of_total) || 0,
                  mentor: payment.mentor?.mentor_name || '',
                  shares: payment.shares || {}
                };
                
                allPayments.push(formattedPayment);
              }
            });
          }
          
          // Process expenses
          if (yearMonthData.expenses) {
            Object.entries(yearMonthData.expenses).forEach(([expenseId, expense]) => {
              if (expenseId.startsWith('expense_') && expense.transaction_type === 'expense') {
                const formattedExpense = {
                  id: expenseId,
                  docId: doc.id,
                  timestamp: expense.metadata?.created_at || '',
                  category: expense.category || 'Unknown',
                  details: expense.details || '',
                  egp_total: Number(expense.egp_total) || 0,
                  admin: expense.register_admin?.name || 'Unknown'
                };
                
                allExpenses.push(formattedExpense);
              }
            });
          }
          
          // Process salaries
          if (yearMonthData.salaries) {
            Object.entries(yearMonthData.salaries).forEach(([salaryId, salary]) => {
              if (salaryId.startsWith('salary_')) {
                const formattedSalary = {
                  id: salaryId,
                  docId: doc.id,
                  timestamp: salary.metadata?.created_at || '',
                  amount_egp: Number(salary.amount_egp) || 0,
                  notes: salary.metadata?.notes || '',
                  receiver: salary.reciever || {}, // Note: spelled 'reciever' in structure
                  register_admin: salary.register_admin || {}
                };
                
                allSalaries.push(formattedSalary);
              }
            });
          }
        });

        // Filter by date range
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        
        const filteredPayments = allPayments.filter(payment => {
          const paymentDate = new Date(payment.timestamp);
          return paymentDate >= startDate && paymentDate <= endDate;
        });
        
        const filteredExpenses = allExpenses.filter(expense => {
          const expenseDate = new Date(expense.timestamp);
          return expenseDate >= startDate && expenseDate <= endDate;
        });
        
        const filteredSalaries = allSalaries.filter(salary => {
          const salaryDate = new Date(salary.timestamp);
          return salaryDate >= startDate && salaryDate <= endDate;
        });
        
        // Process analytics data with salary deductions
        processAnalyticsData(filteredPayments, filteredExpenses, filteredSalaries);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
        setError('Failed to load analytics data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) {
      fetchAnalyticsData();
    }
  }, [isAdmin, dateRange]);

  // # Fetch salary deductions for the date range
  const fetchSalaryDeductions = async (startDate, endDate) => {
    try {
      // # First try to get data from the new structure (year-based)
      const years = new Set();
      // Add all years in the date range
      let currentYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();
      while (currentYear <= endYear) {
        years.add(currentYear.toString());
        currentYear++;
      }
      
      let allDeductions = [];
      
      // # Fetch data for each year in the range
      for (const year of years) {
        const yearDocRef = query(collection(db, 'adm_income'), where('year', '==', year));
        const yearDocSnap = await getDocs(yearDocRef);
        
        if (yearDocSnap.docs.length > 0) {
          const yearData = yearDocSnap.docs[0].data();
          
          // # Process each team member's salaries
          Object.entries(yearData).forEach(([adminId, adminData]) => {
            // Skip the budget entry
            if (adminId === 'budget') return;
            
            if (adminData && adminData.salaries) {
              Object.entries(adminData.salaries).forEach(([key, deduction]) => {
                const deductionDate = new Date(deduction.timestamp);
                
                // # Check if deduction is within date range
                if (deductionDate >= startDate && deductionDate <= endDate) {
                  allDeductions.push({
                    id: key,
                    admin_id: adminId,
                    admin_name: adminData.name || adminId,
                    ...deduction
                  });
                }
              });
            }
          });
        }
      }
      
      // # If no data found in new structure, try the old structure
      if (allDeductions.length === 0) {
        const admIncomeRef = collection(db, 'adm_income');
        const admIncomeSnapshot = await getDocs(admIncomeRef);
        
        admIncomeSnapshot.forEach(doc => {
          const adminData = doc.data();
          
          // # Process salary deductions if they exist
          if (adminData.salary_deducts) {
            Object.entries(adminData.salary_deducts).forEach(([key, deduction]) => {
              // # Check if deduction is within date range
              const deductionDate = new Date(deduction.timestamp);
              
              if (deductionDate >= startDate && deductionDate <= endDate) {
                allDeductions.push({
                  id: key,
                  admin_id: doc.id,
                  admin_name: adminData.name || doc.id,
                  ...deduction
                });
              }
            });
          }
        });
      }
      
      // # Sort by timestamp
      allDeductions.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      return allDeductions;
    } catch (error) {
      console.error('Error fetching salary deductions:', error);
      return [];
    }
  };

  // # Process analytics data
  const processAnalyticsData = (payments, expenses, salaries) => {
    try {
      // Group payments by month
      const paymentsByMonth = {};
      payments.forEach(payment => {
        const date = new Date(payment.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!paymentsByMonth[monthKey]) {
          paymentsByMonth[monthKey] = {
            month: monthKey,
            revenue: 0,          // Total revenue (egp_total)
            netRevenue: 0,       // Net revenue (egp_net)
            budget: 0,           // Budget allocation (budget_dvd_amount)
            salaryDeductions: 0, // Salary payments
            availableCash: 0,    // Net revenue minus salary deductions
            expenses: 0,         // Expenses from budget
            budgetBalance: 0     // Budget minus expenses
          };
        }
        
        // Add revenue data
        paymentsByMonth[monthKey].revenue += payment.egp_total;
        paymentsByMonth[monthKey].netRevenue += payment.egp_net;
        paymentsByMonth[monthKey].budget += payment.budget_dvd_amount;
        
        // Add product-specific revenue
        const product = payment.product;
        if (product) {
          paymentsByMonth[monthKey][product] = (paymentsByMonth[monthKey][product] || 0) + payment.egp_total;
        }
      });
      
      // Add expenses by month
      expenses.forEach(expense => {
        const date = new Date(expense.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!paymentsByMonth[monthKey]) {
          paymentsByMonth[monthKey] = {
            month: monthKey,
            revenue: 0,
            netRevenue: 0,
            budget: 0,
            salaryDeductions: 0,
            availableCash: 0,
            expenses: 0,
            budgetBalance: 0
          };
        }
        
        paymentsByMonth[monthKey].expenses += expense.egp_total;
      });
      
      // Add salary deductions by month
      salaries.forEach(salary => {
        const date = new Date(salary.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!paymentsByMonth[monthKey]) {
          paymentsByMonth[monthKey] = {
            month: monthKey,
            revenue: 0,
            netRevenue: 0,
            budget: 0,
            salaryDeductions: 0,
            availableCash: 0,
            expenses: 0,
            budgetBalance: 0
          };
        }
        
        paymentsByMonth[monthKey].salaryDeductions += salary.amount_egp;
      });
      
      // Calculate budget balance and available cash for each month
      Object.values(paymentsByMonth).forEach(month => {
        // Budget balance is budget allocation minus expenses
        month.budgetBalance = month.budget - month.expenses;
        
        // Available cash is net revenue minus salary deductions
        month.availableCash = month.netRevenue - month.salaryDeductions;
      });
      
      // Convert to array and sort by month
      const revenueOverTime = Object.values(paymentsByMonth).sort((a, b) => 
        a.month.localeCompare(b.month)
      );
      
      // Calculate monthly growth rates
      const monthlyGrowth = [];
      for (let i = 1; i < revenueOverTime.length; i++) {
        const prevMonth = revenueOverTime[i - 1];
        const currentMonth = revenueOverTime[i];
        
        const growthRate = prevMonth.revenue > 0 
          ? ((currentMonth.revenue - prevMonth.revenue) / prevMonth.revenue) * 100 
          : 0;

        monthlyGrowth.push({
          month: currentMonth.month,
          growth: growthRate,
          previousRevenue: prevMonth.revenue,
          currentRevenue: currentMonth.revenue
        });
      }

      // Calculate totals
      const totalGrossRevenue = payments.reduce((sum, payment) => sum + payment.egp_total, 0);
      const totalNetRevenue = payments.reduce((sum, payment) => sum + payment.egp_net, 0);
      const totalBudget = payments.reduce((sum, payment) => sum + payment.budget_dvd_amount, 0);
      const totalExpenses = expenses.reduce((sum, expense) => sum + expense.egp_total, 0);
      const totalSalaryDeductions = salaries.reduce((sum, salary) => sum + salary.amount_egp, 0);
      
      // Budget balance is budget allocation minus expenses
      const budgetBalance = totalBudget - totalExpenses;
      
      // Available cash is net revenue minus salary deductions
      const availableCash = totalNetRevenue - totalSalaryDeductions;
      
      // Calculate monthly average and growth rate
      const monthCount = revenueOverTime.length;
      const monthlyAverage = monthCount > 0 ? totalGrossRevenue / monthCount : 0;
      
      // Calculate overall growth rate
      let growthRate = 0;
      if (revenueOverTime.length >= 2) {
        const firstMonth = revenueOverTime[0];
        const lastMonth = revenueOverTime[revenueOverTime.length - 1];
        
        if (firstMonth.revenue > 0) {
          growthRate = ((lastMonth.revenue - firstMonth.revenue) / firstMonth.revenue) * 100;
        }
      }
      
      // Prepare financial health data
      const financialHealthData = {
        netProfitMargin: revenueOverTime.map(month => ({
          month: month.month,
          revenue: month.revenue,
          netRevenue: month.netRevenue,
          totalDeductions: month.budget + month.salaryDeductions,
          margin: month.revenue > 0 
            ? ((month.netRevenue - month.salaryDeductions) / month.revenue) * 100 
            : 0
        })),
        burnRate: revenueOverTime.map(month => ({
          month: month.month,
          revenue: month.revenue,
          expenses: month.expenses,
          salaries: month.salaryDeductions,
          rate: month.revenue > 0 
            ? ((month.expenses + month.salaryDeductions) / month.revenue) * 100
            : 0
        })),
        monthlyRevenue: revenueOverTime.map(month => ({
          month: month.month,
          amount: month.revenue
        })),
        monthlyExpenses: revenueOverTime.map(month => ({
          month: month.month,
          amount: month.expenses
        })),
        monthlySalaryDeductions: revenueOverTime.map(month => ({
          month: month.month,
          amount: month.salaryDeductions
        }))
      };

      // Update analytics data
      setAnalyticsData({
        revenueOverTime,
        monthlyGrowth,
        financialHealthData,
        rawPayments: payments,
        stats: {
          totalRevenue: totalGrossRevenue,
          totalNetRevenue: totalNetRevenue,
          totalBudget: totalBudget,
          totalSalaryDeductions: totalSalaryDeductions,
          totalExpenses: totalExpenses,
          budgetBalance: budgetBalance,
          availableCash: availableCash,
          monthlyAverage: monthlyAverage,
          growthRate: growthRate,
          projectedAnnual: monthlyAverage * 12
        }
      });
    } catch (error) {
      console.error('Error processing analytics data:', error);
      setError('Failed to process analytics data. Please try again later.');
      
      // Set default empty data
      setAnalyticsData({
        revenueOverTime: [],
        monthlyGrowth: [],
        financialHealthData: {
          netProfitMargin: [],
          burnRate: [],
          monthlyRevenue: [],
          monthlyExpenses: [],
          monthlySalaryDeductions: []
        },
        stats: {
          totalRevenue: 0,
          totalNetRevenue: 0,
          totalBudget: 0,
          totalSalaryDeductions: 0,
          totalExpenses: 0,
          budgetBalance: 0,
          availableCash: 0,
          monthlyAverage: 0,
          growthRate: 0,
          projectedAnnual: 0
        }
      });
    }
  };

  // # Handle date filter changes
  const handleTempDateChange = (e) => {
    const { name, value } = e.target;
    setTempDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilter = () => {
    setDateRange(tempDateRange);
    setIsFilterDialogOpen(false);
  };

  const handleCancelFilter = () => {
    setTempDateRange(dateRange);
    setIsFilterDialogOpen(false);
  };

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  return (
    <div className="fnc_rev_page">
      <SharedHeader />
      
      <main className="fnc_rev_content">
        <div className="fnc_rev_header">
          <h1>Net Revenue Analytics</h1>
          <div className="fnc_rev_header-controls">
            <button 
              className="fnc_rev_advanced-button"
              onClick={() => setShowAdvancedAnalytics(!showAdvancedAnalytics)}
            >
              <FaChartArea /> {showAdvancedAnalytics ? 'Basic Analytics' : 'Advanced Analytics'}
            </button>
            <button 
              className="fnc_rev_filter-button"
              onClick={() => setIsFilterDialogOpen(true)}
            >
              <FaFilter /> Filter by Date
            </button>
          </div>
        </div>

        {showAdvancedAnalytics ? (
          <NetRevenueExtra />
        ) : (
          <>
            {/* Analytics Tabs */}
            <div className="fnc_rev_tabs">
              <button 
                className={`fnc_rev_tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <FaChartLine /> Overview Analytics
              </button>
              <button 
                className={`fnc_rev_tab ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveTab('products')}
              >
                <FaChartPie /> Product Analysis
              </button>
              <button 
                className={`fnc_rev_tab ${activeTab === 'trends' ? 'active' : ''}`}
                onClick={() => setActiveTab('trends')}
              >
                <FaChartBar /> Trends
              </button>
              <button 
                className={`fnc_rev_tab ${activeTab === 'health' ? 'active' : ''}`}
                onClick={() => setActiveTab('health')}
              >
                <FaHeartbeat /> Financial Health
              </button>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="fnc_rev_loading">Loading analytics data...</div>
            ) : (
              <div className="fnc_rev_dashboard">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="fnc_rev_section">
                    {/* Overview Stats */}
                    <div className="fnc_rev_stats-grid">
                      <div className="fnc_rev_stat-card">
                        <FaMoneyBillWave className="fnc_rev_stat-icon" />
                        <h3>Total Revenue</h3>
                        <p className="fnc_rev_stat-value">
                          E£ {analyticsData.stats.totalRevenue.toLocaleString()}
                        </p>
                      </div>
                      <div className="fnc_rev_stat-card">
                        <FaPercentage className="fnc_rev_stat-icon" />
                        <h3>Budget Allocation</h3>
                        <p className="fnc_rev_stat-value">
                          E£ {analyticsData.stats.totalBudget.toLocaleString()}
                        </p>
                      </div>
                      <div className="fnc_rev_stat-card">
                        <FaDollarSign className="fnc_rev_stat-icon" />
                        <h3>Net Revenue</h3>
                        <p className="fnc_rev_stat-value">
                          E£ {analyticsData.stats.totalNetRevenue.toLocaleString()}
                        </p>
                      </div>
                      <div className="fnc_rev_stat-card">
                        <FaCalendarAlt className="fnc_rev_stat-icon" />
                        <h3>Salary Deductions</h3>
                        <p className="fnc_rev_stat-value">
                          E£ {analyticsData.stats.totalSalaryDeductions.toLocaleString()}
                        </p>
                      </div>
                      <div className="fnc_rev_stat-card">
                        <FaWallet className="fnc_rev_stat-icon" />
                        <h3>Available Cash</h3>
                        <p className="fnc_rev_stat-value">
                          E£ {analyticsData.stats.availableCash.toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {/* Add Revenue Run Chart */}
                    <div className="fnc_rev_run-chart">
                      <h3>Revenue Run Rate</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={analyticsData.revenueOverTime}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip 
                            formatter={(value) => `E£ ${value.toLocaleString()}`}
                            labelFormatter={(label) => `Month: ${label}`}
                          />
                          <Legend />
                          <Area 
                            type="monotone" 
                            dataKey="revenue" 
                            stackId="1"
                            stroke="#8884d8" 
                            fill="#8884d8" 
                            fillOpacity={0.3}
                            name="Total Revenue"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="netRevenue" 
                            stackId="2"
                            stroke="#82ca9d" 
                            fill="#82ca9d" 
                            fillOpacity={0.3}
                            name="Net Revenue"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="budget" 
                            stackId="3"
                            stroke="#ffc658" 
                            fill="#ffc658" 
                            fillOpacity={0.3}
                            name="Budget"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="expenses" 
                            stackId="4"
                            stroke="#ff8042" 
                            fill="#ff8042" 
                            fillOpacity={0.3}
                            name="Expenses"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Revenue Analysis Chart */}
                    <div className="fnc_rev_chart-container">
                      <h3>Revenue Analysis Over Time</h3>
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={analyticsData.revenueOverTime}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip 
                            formatter={(value) => `E£ ${value.toLocaleString()}`}
                            labelFormatter={(label) => `Month: ${label}`}
                          />
                          <Legend />
                          {/* Revenue Metrics */}
                          <Line type="monotone" dataKey="revenue" stroke="#8884d8" name="Total Revenue" strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="netRevenue" stroke="#82ca9d" name="Net Revenue" strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="budget" stroke="#ffc658" name="Budget" strokeWidth={2} dot={{ r: 4 }} />
                          
                          {/* Deductions & Expenses */}
                          <Line type="monotone" dataKey="expenses" stroke="#ff8042" name="Budget Expenses" strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="salaryDeductions" stroke="#ff7300" name="Salary Deductions" strokeWidth={2} dot={{ r: 4 }} />
                          
                          {/* Balance Metrics */}
                          <Line type="monotone" dataKey="budgetBalance" stroke="#0088FE" name="Budget Balance" strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="availableCash" stroke="#00C49F" name="Available Cash" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Product Analysis Tab */}
                {activeTab === 'products' && (
                  <NetProductAnalysis 
                    analyticsData={{
                      revenueOverTime: analyticsData.revenueOverTime,
                      currencyDistribution: [], // Add if you have this data
                      stats: analyticsData.stats,
                      rawPayments: analyticsData.rawPayments
                    }} 
                  />
                )}

                {/* Trends Tab */}
                {activeTab === 'trends' && (
                  <TrendsAnalysis 
                    analyticsData={{
                      revenueOverTime: analyticsData.revenueOverTime,
                      monthlyGrowth: analyticsData.monthlyGrowth,
                      stats: analyticsData.stats
                    }} 
                  />
                )}

                {/* Financial Health Tab */}
                {activeTab === 'health' && (
                  <FinancialHealth financialData={analyticsData.financialHealthData} />
                )}
              </div>
            )}

            {/* Date Filter Dialog */}
            {isFilterDialogOpen && (
              <div className="fnc_rev_dialog-overlay" onClick={handleCancelFilter}>
                <div className="fnc_rev_dialog" onClick={e => e.stopPropagation()}>
                  <div className="fnc_rev_dialog-header">
                    <h3>Filter by Date Range</h3>
                    <button 
                      className="fnc_rev_dialog-close"
                      onClick={handleCancelFilter}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  
                  <div className="fnc_rev_dialog-content">
                    <div className="fnc_rev_filter-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        name="startDate"
                        value={tempDateRange.startDate}
                        onChange={handleTempDateChange}
                      />
                    </div>
                    <div className="fnc_rev_filter-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        name="endDate"
                        value={tempDateRange.endDate}
                        onChange={handleTempDateChange}
                      />
                    </div>
                  </div>

                  <div className="fnc_rev_dialog-footer">
                    <button 
                      className="fnc_rev_dialog-button secondary"
                      onClick={handleCancelFilter}
                    >
                      Cancel
                    </button>
                    <button 
                      className="fnc_rev_dialog-button primary"
                      onClick={handleApplyFilter}
                    >
                      Apply Filter
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default RevenueAnalytics; 